import { useCallback, useMemo } from "react";
import { runAutoGenFilterPipeline } from "../utils/pipeline";
import { flattenFilterConfigs } from "../utils/flattenFilterConfigs";

const OVERVIEW_CONFIG = {
  templates: {
    id: "templates",
    display: "Templates",
    valuePath: "metrics.templates",
  },
  scopes: {
    id: "scopes",
    display: "Scopes",
    valuePath: "metrics.scopes",
  },
  totalFilters: {
    id: "totalFilters",
    display: "Total Filters",
    valuePath: "metrics.totalFilters",
  },
  annotationFilters: {
    id: "annotationFilters",
    display: "Annotation Filters",
    valuePath: "metrics.annotationFilters",
  },
  documentFilters: {
    id: "documentFilters",
    display: "Document Filters",
    valuePath: "metrics.documentFilters",
  },
  tenantMode: {
    id: "tenantMode",
    display: "Tenant Mode",
    valuePath: "meta.tenantMode",
  },
};

export const usePipelineData = ({
  selectedTpls,
  allTemplates,
  filterScopes,
  selfManaged,
  annotationCounts,
  unifiedView,
  smTagTypesConfig,
  flatDocFilters = false,
  hasNewTopicModelling = false,
  isSplitMode = false,
}) => {
  const pipelineOutput = useMemo(() => {
    if (!selectedTpls.length) return null;

    return runAutoGenFilterPipeline({
      selectedTpls,
      allTpls: allTemplates,
      filterScopes,
      isSelfManagedTenant: selfManaged,
      annotationTagsCount: annotationCounts,
      smTagTypesConfig,
      flatDocFilters,
      hasNewTopicModelling,
      isSplitMode,
    });
  }, [selectedTpls, filterScopes, selfManaged, annotationCounts, allTemplates, flatDocFilters, hasNewTopicModelling, isSplitMode]);

  const finalSections = pipelineOutput?.result || [];
  const metadataSection = pipelineOutput?.finalSections?.metadata;
  const annotationSection = pipelineOutput?.finalSections?.annotation;
  const documentSection = pipelineOutput?.finalSections?.document;

  const flattenFilters = useCallback((sections = []) => flattenFilterConfigs(sections), []);

  const flattenedAnnotationFilters = useMemo(
    () => flattenFilters(annotationSection ? [annotationSection] : []),
    [annotationSection, flattenFilters]
  );

  const flattenedDocumentFilters = useMemo(
    () => flattenFilters(documentSection ? [documentSection] : []),
    [documentSection, flattenFilters]
  );

  const unifiedFilters = useMemo(() => {
    if (!unifiedView) return [];
    return flattenFilters(finalSections);
  }, [unifiedView, finalSections, flattenFilters]);

  const totalFilters = flattenedAnnotationFilters.length + flattenedDocumentFilters.length;
  const hasGeneratedFilters = finalSections.length > 0;

  const stageData = useMemo(
    () => [
      {
        id: "input",
        title: "Input: Selected Templates",
        description:
          "Raw template configurations that feed the pipeline. Inspect these to trace value paths and annotation settings.",
        data: selectedTpls,
        defaultOpen: false,
      },
      {
        id: "stage1",
        title: "Step 1 – extractAndOrganizeBlocks",
        description:
          "Blocks grouped into annotation, document, rollup, and value-path buckets per template.",
        data: pipelineOutput?.extractedBlocks,
        defaultOpen: true,
      },
      {
        id: "stage2",
        title: "Step 2 – processAuthorAndCommonFilters",
        description:
          "Common filters (authors, published date, author tags) added before template-specific logic.",
        data: pipelineOutput?.authorAndCommonFilters,
      },
      {
        id: "stage3",
        title: "Step 3 – compareAndGroupBlocks",
        description:
          "Potential filters grouped by their shared content types before deduplication.",
        data: pipelineOutput?.groupedBlocks,
      },
      {
        id: "stage3b",
        title: "Step 3b – TEMP_removeDuplicateFilters",
        description:
          "Temporary deduplication step to collapse repeated tag and rollup filters.",
        data: pipelineOutput?.deduplicatedGroups,
      },
      {
        id: "stage4",
        title: "Step 4 – buildFilterConfigurations",
        description:
          "Section objects generated for the filter UI, ready to plug into panels.",
        data: pipelineOutput?.builtSections,
      },
      {
        id: "stage5",
        title: "Step 5 – final section assembly",
        description:
          "Metadata, annotation, and document filter sections composed with ordering rules (self-managed vs default).",
        data: pipelineOutput?.finalSections,
      },
      {
        id: "stage6",
        title: "Step 6 – final result",
        description:
          "Output returned by useAutoGenFilterConfigsFromTpl before optional unified flattening.",
        data: pipelineOutput?.result,
        defaultOpen: true,
      },
      {
        id: "stage6b",
        title: "Unified filter group",
        description: "Flattened filters identical to the hook's unifiedFilterGroup mode.",
        data: unifiedFilters,
        defaultOpen: true,
        hidden: !unifiedView,
      },
    ],
    [pipelineOutput, selectedTpls, unifiedFilters, unifiedView]
  );

  const overviewData = useMemo(
    () => ({
      metrics: {
        templates: selectedTpls.length,
        scopes: filterScopes.length,
        totalFilters,
        annotationFilters: flattenedAnnotationFilters.length,
        documentFilters: flattenedDocumentFilters.length,
      },
      meta: {
        tenantMode: selfManaged ? "Self-managed" : "Default",
      },
    }),
    [
      selectedTpls.length,
      filterScopes.length,
      totalFilters,
      flattenedAnnotationFilters.length,
      flattenedDocumentFilters.length,
      selfManaged,
    ]
  );

  return {
    pipelineOutput,
    finalSections,
    metadataSection,
    annotationSection,
    documentSection,
    unifiedFilters,
    totalFilters,
    hasGeneratedFilters,
    stageData,
    overviewData,
    overviewConfig: OVERVIEW_CONFIG,
  };
};


